import React, {
  FC,
  ReactNode,
  useRef,
  useState,
  useEffect,
  useMemo,
  CSSProperties
} from "react";
import { Navigation } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/free-mode";
import { Swiper as SwiperClass } from "swiper/types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faArrowRight } from "@fortawesome/free-solid-svg-icons";
import styles from "./styles.module.scss";

interface Props {
  id?: string;
  slides: Array<ReactNode>;
  className?: string;
  baseId: string;
  centerInsufficientSlides?: boolean;
  style?: CSSProperties;
  /** When true, Swiper navigation is enabled at every breakpoint (default: only lg+). */
  enableNavigationAllBreakpoints?: boolean;
}

const SPACE_BETWEEN = 16;

/**
 * A carousel built around react-swiper with smooth scrolling
 * @param props
 * @constructor
 */
const Carousel: FC<Props> = (props) => {
  const swiperRef = useRef<SwiperClass | null>(null);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);
  const [allSlidesVisible, setAllSlidesVisible] = useState(false);

  if (!props.slides) {
    return null;
  }

  const updateNavigationState = (swiper: SwiperClass) => {
    setIsBeginning(swiper.isBeginning);
    setIsEnd(swiper.isEnd);
    setAllSlidesVisible(swiper.isBeginning && swiper.isEnd);
  };

  const handleResize = () => {
    if (swiperRef.current) {
      setTimeout(() => {
        updateNavigationState(swiperRef.current!);
      }, 500);
    }
  };

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    if (swiperRef.current) {
      updateNavigationState(swiperRef.current);
    }
  }, [props.slides]);

  const navigation = useMemo(() => {
    return {
      nextEl: `.swiper-button-next-${props.baseId}`,
      prevEl: `.swiper-button-prev-${props.baseId}`
    };
  }, [props.baseId]);

  const style = useMemo<CSSProperties>(() => {
    return {
      position: "relative",
      ...(props.style || {})
    };
  }, [props.style]);

  const navigationEnabled = props.enableNavigationAllBreakpoints === true;

  const breakpoints = useMemo(
    () => ({
      0: {
        slidesPerView: 1,
        slidesPerGroup: 1,
        spaceBetween: SPACE_BETWEEN,
        centeredSlides: false,
        navigation: navigationEnabled
      },
      576: {
        slidesPerView: 1,
        slidesPerGroup: 1,
        spaceBetween: SPACE_BETWEEN,
        centeredSlides: false,
        navigation: navigationEnabled
      },
      768: {
        slidesPerView: 2.1,
        slidesPerGroup: 2,
        spaceBetween: SPACE_BETWEEN,
        centeredSlides: false,
        navigation: navigationEnabled
      },
      992: {
        slidesPerView: "auto" as const,
        spaceBetween: SPACE_BETWEEN,
        centeredSlides: false,
        navigation: true
      },
      1200: {
        slidesPerView: "auto" as const,
        spaceBetween: SPACE_BETWEEN,
        centeredSlides: false,
        navigation: true
      }
    }),
    [navigationEnabled]
  );

  return (
    <div id="online-posa-carousel" className={styles.carouselRoot}>
      <div style={style} className={props.className}>
        <Swiper
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
          }}
          onInit={handleResize}
          onSlideChange={(swiper) => {
            updateNavigationState(swiper);
          }}
          modules={[Navigation]}
          breakpoints={breakpoints}
          autoHeight={true}
          centeredSlides={false}
          centerInsufficientSlides={props.centerInsufficientSlides}
          draggable={true}
          loop={false}
          pagination={false}
          scrollbar={false}
          mousewheel={{
            releaseOnEdges: false,
            sensitivity: 1,
            thresholdDelta: 50
          }}
          touchRatio={1.5}
          touchAngle={45}
          threshold={5}
          navigation={navigation}
          style={{
            paddingRight: SPACE_BETWEEN,
            paddingTop: 10,
            paddingBottom: 10
          }}
        >
          {props.slides.map((slide, index) => (
            <SwiperSlide
              key={`${props.baseId}${index}`}
              style={{
                marginRight:
                  index === props.slides.length - 1
                    ? 2 * SPACE_BETWEEN
                    : "unset",
                marginLeft: 3
              }}
              className={styles.swiperSlide}
            >
              {slide}
            </SwiperSlide>
          ))}
        </Swiper>

        {!isBeginning && !allSlidesVisible && (
          <div
            className={`swiper-button-prev-${props.baseId} ${styles.carouselNavButton} ${styles.carouselNavButtonPrev}`}
            onClick={() => swiperRef.current?.slidePrev()}
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </div>
        )}

        {!isEnd && !allSlidesVisible && (
          <div
            className={`swiper-button-next-${props.baseId} ${styles.carouselNavButton} ${styles.carouselNavButtonNext}`}
            onClick={() => swiperRef.current?.slideNext()}
          >
            <FontAwesomeIcon icon={faArrowRight} />
          </div>
        )}
      </div>
    </div>
  );
};

export default Carousel;
